import {
  Box,
  Calendar1Icon,
  DeleteIcon,
  Divider,
  DocumentIcon,
  Down1Icon,
  Edit1Icon,
  GKButton,
  GKPopover,
  Grid2,
  IconButton,
  PopConfirm,
  Show,
  StarFillIcon,
  StarIcon,
  Tab,
  Tabs,
  Typography,
  VectorIcon,
} from "components";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { ModelPortfolioObjectInterface } from "interfaces/EquityBasket";
import DashboardLayout from "layouts/DashboardLayout";
import React, { useState } from "react";
import { numberToCurrency } from "utils/MathFunction";
import ModelBankLedger from "./ModelBankLedger";
import ModelHoldings from "./ModelHoldings";
import ModelOverview from "./ModelOverview";
import ModelTradebook from "./ModelTradebook";
import RebalanceModal from "./RebalanceModal";
import VersionDetailsModal from "./VersionDetailsModal";
import VersionUploadModal from "./VersionUploadModal";
import useModelPortfolioVersion from "./useModelPortfolioVersion";

const ViewModelPortfolioVersion = ({
  selectedModelPortfolio,
}: {
  selectedModelPortfolio?: ModelPortfolioObjectInterface;
}) => {
  const {
    openUploadModal,
    setOpenUploadModal,
    form,
    tabs,
    setTabs,
    getDataByIdLoading,
    modalPortfolioDelete,
    modelPortdolioData,
    getAllOldModels,
    oldIdSelected,
    modalPortfolioDefault,
    navigate,
    modelPortfolioId,
    id,
    openSelectVersionModal,
    setOpenSelectVersionModal,
  } = useModelPortfolioVersion({
    selectedModelPortfolio,
  });

  const [openModelToRebalance, setOpenModelToRebalance] = useState(false);

  const [versionToPreview, setVersionToPreview] = useState<any>(null);

  const [deleteConfirmation, setDeleteConfirmation] =
    React.useState<HTMLElement | null>(null);

  return (
    <DashboardLayout
      title="Add Model Portfolio"
      loading={getDataByIdLoading}
      onlyChildren={Boolean(selectedModelPortfolio?.id)}
    >
      <Grid2 container spacing={1}>
        <Grid2
          size={12}
          container
          justifyContent={"space-between"}
          alignItems={"center"}
          display={!selectedModelPortfolio?.id ? "flex" : "none"}
        >
          <Typography fontWeight={600} fontSize={16}>
            Model Portfolio
          </Typography>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <IconButton
              color="error"
              onClick={(e) => setDeleteConfirmation(e.currentTarget)}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              sx={{ color: "var(--black)" }}
              onClick={() => {
                modalPortfolioDefault(modelPortfolioId);
              }}
              disabled={modelPortdolioData?.data?.primary}
            >
              {modelPortdolioData?.data?.primary ? (
                <StarFillIcon />
              ) : (
                <StarIcon />
              )}
            </IconButton>
            <Show.When isTrue={modelPortdolioData?.data?.isEditable}>
              <IconButton
                sx={{ color: "var(--black)" }}
                onClick={() => {
                  navigate(
                    `/app/equity-basket/${id}/model-portfolio/${modelPortfolioId}/edit`,
                  );
                }}
              >
                <Edit1Icon />
              </IconButton>
            </Show.When>
            <Divider orientation="vertical" sx={{ height: "30px" }} />
            <GKButton
              startIcon={<VectorIcon />}
              onClick={() => setOpenModelToRebalance(true)}
            >
              Rebalance
            </GKButton>
          </Box>
        </Grid2>
        <Grid2
          container
          size={12}
          bgcolor={"#F8F8F8"}
          padding={2}
          borderRadius={2}
        >
          <Grid2 size={6} display={"flex"} flexDirection={"column"}>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <DocumentIcon />
              <Typography
                fontWeight={400}
                fontSize={14}
                color={"var(--neutral-500)"}
              >
                Model Version:
              </Typography>
              <Typography fontWeight={600} fontSize={14}>
                {modelPortdolioData?.data?.version || "-"}
              </Typography>
              <IconButton
                sx={{ background: "#E0E0E0", color: "var(--black)" }}
                onClick={(e) => setOpenSelectVersionModal(e.currentTarget)}
              >
                <Down1Icon />
              </IconButton>
            </Box>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Calendar1Icon />
              <Typography
                fontWeight={400}
                fontSize={14}
                color={"var(--neutral-500)"}
              >
                Creation Date:
              </Typography>
              <Typography fontWeight={600} fontSize={14}>
                {dayjs(modelPortdolioData?.data?.date).format("DD MMM YYYY")}
              </Typography>
            </Box>
          </Grid2>
          <Grid2
            size={6}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"flex-end"}
            gap={2}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
              }}
            >
              <Typography
                fontWeight={400}
                fontSize={14}
                color={"var(--neutral-500)"}
              >
                Minimum Investment Value
              </Typography>
              <Typography fontWeight={600} fontSize={14}>
                {numberToCurrency(
                  modelPortdolioData?.data?.minimumInvestement,
                ) || "-"}
              </Typography>
            </Box>
            <Divider orientation="vertical" />
            <Box
              style={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
              }}
            >
              <Typography
                fontWeight={400}
                fontSize={14}
                color={"var(--neutral-500)"}
              >
                XIRR
              </Typography>
              <Typography fontWeight={600} fontSize={14}>
                {modelPortdolioData?.data?.xirr || "-"}
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 size={12}>
          <Tabs
            value={tabs}
            onChange={(e, val) => {
              setTabs(val);
            }}
          >
            <Tab label="Overview" />
            <Tab label="Holdings" />
            <Tab label="Trade Book" />
            <Tab label="Ledger" />
          </Tabs>
          <Divider />
        </Grid2>
        <Grid2 size={12}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Show.When isTrue={tabs === 0}>
              <ModelOverview
                selectedId={modelPortfolioId}
                oldIdSelected={oldIdSelected}
                selectedModelPortfolio={selectedModelPortfolio}
              />
            </Show.When>
            <Show.When isTrue={tabs === 1}>
              <ModelHoldings
                selectedId={modelPortfolioId}
                oldIdSelected={oldIdSelected}
              />
            </Show.When>
            <Show.When isTrue={tabs === 2}>
              <ModelTradebook
                selectedId={modelPortfolioId}
                oldIdSelected={oldIdSelected}
              />
            </Show.When>
            <Show.When isTrue={tabs === 3}>
              <ModelBankLedger
                selectedId={modelPortfolioId}
                oldIdSelected={oldIdSelected}
              />
            </Show.When>
          </motion.div>
        </Grid2>
      </Grid2>

      {openUploadModal && (
        <VersionUploadModal
          open={openUploadModal}
          form={form}
          setOpen={setOpenUploadModal}
        />
      )}

      {deleteConfirmation && (
        <PopConfirm
          anchorEl={deleteConfirmation}
          open={Boolean(deleteConfirmation)}
          setOpen={setDeleteConfirmation}
          onSuccess={() => modalPortfolioDelete(modelPortfolioId)}
        />
      )}

      <Show.When isTrue={openModelToRebalance}>
        <RebalanceModal
          setOpen={setOpenModelToRebalance}
          open={openModelToRebalance}
          selectedId={selectedModelPortfolio?.id}
        />
      </Show.When>
      <Show.When isTrue={Boolean(openSelectVersionModal)}>
        <GKPopover
          anchorEl={openSelectVersionModal}
          setOpen={setOpenSelectVersionModal}
          open={Boolean(openSelectVersionModal)}
          width={"24%"}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {getAllOldModels?.data?.data?.map((item: any) => {
              return (
                <GKButton
                  fullWidth
                  key={item?.id}
                  color="dark"
                  sx={{
                    borderBottom: "solid 1px #E0E0E0",
                    borderRadius: 0,
                    justifyContent: "flex-start",
                    p: 1.5,
                  }}
                  onClick={() => {
                    // setOldIdSelected(item);
                    setOpenSelectVersionModal(null);
                    if (item?.snapshotVersion !== Number(modelPortfolioId)) {
                      setVersionToPreview(item);
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      fontWeight={600}
                      fontSize={14}
                      width={"100%"}
                      textAlign={"left"}
                    >
                      {item?.snapshotName}
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={12}
                      width={"100%"}
                      textAlign={"left"}
                    >
                      {dayjs(item?.date).format("DD MMM YYYY")}
                    </Typography>
                  </Box>
                </GKButton>
              );
            })}
          </Box>
        </GKPopover>
      </Show.When>
      <Show.When isTrue={Boolean(versionToPreview)}>
        <VersionDetailsModal
          open={Boolean(versionToPreview)}
          setOpen={() => setVersionToPreview(null)}
          selectedVersion={versionToPreview}
        />
      </Show.When>
    </DashboardLayout>
  );
};

export default ViewModelPortfolioVersion;
